import * as Accountant from 'app/calcs/Accountant'
import { FormOfPaymentOptions } from 'app/calcs/pensions'
import { IMonthlyPensionPayments, SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces'
import { MaterialInput, RadioGroup } from 'app/components'
import { RetirementAgeSelector } from 'app/components/form/RetirementAgeSelector';
import { FormOfPayment } from 'app/components/questions/FormOfPayment'
import { IProfileContainerState, PensionQuestionsGroupContainer } from 'app/containers/PipelineSection/Pension/Container';
import { IQuestionsGroupContainerProps, QuestionsGroupContainer } from 'app/containers/PipelineSection/QuestionGroupContainer'
import { INcpersPensionInputs, INcpersRunCalculationsRequest, NcpersActions } from 'app/redux/Customers/ncpers/actions'
import { IRetirementPerson, isSamePersonForSSA, Profile } from 'app/redux/Profile/model'
import { WorkbookServiceResponse } from 'app/services/models/Workbook'
import * as utils from 'app/utils'
import { RequestCache } from 'app/utils/RequestCache'
import { getBrandHelpers } from 'config'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { Button, ButtonGroup, Col, FormGroup, ListGroup, ListGroupItem, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import MaterialNumber from 'app/components/material/MaterialNumber';

interface ICurrentPensionContainerState extends IProfileContainerState {
  headOfHousehold: IRetirementPerson
  formOfPayment: SimplePensionFormOfPayment
  currentSalary: number
  oneYearsAgoSalary: number
  twoYearsAgoSalary: number
  yearsOfService: number,
  beginPaymentsAge: moment.Duration
  monthlyPayout: string
}

class CurrentPensionContainer extends PensionQuestionsGroupContainer<IQuestionsGroupContainerProps, ICurrentPensionContainerState> {
  currentSalaryNS = 'pension.ncpers.salaries[0]'
  oneYearsAgoSalaryNS = 'pension.ncpers.salaries[1]'
  twoYearsAgoSalaryNS = 'pension.ncpers.salaries[2]'
  yearsOfServiceNS = 'pension.ncpers.yearsOfService'
  formOfPaymentNS = 'pension.ncpers.formOfPayment'
  beginPaymentsAgeNS = 'pension.ncpers.beginPaymentsAge'

  state: ICurrentPensionContainerState = {
    profile: null,
    headOfHousehold: null,
    formOfPayment: this.getValueForQuestion(this.formOfPaymentNS) || SimplePensionFormOfPayment.singleLifeOnly,
    currentSalary: this.getValueForQuestion(this.currentSalaryNS) || 0,
    oneYearsAgoSalary: this.getValueForQuestion(this.oneYearsAgoSalaryNS) || 0,
    twoYearsAgoSalary: this.getValueForQuestion(this.twoYearsAgoSalaryNS) || 0,
    yearsOfService: this.getValueForQuestion(this.yearsOfServiceNS) || 0,
    monthlyPayout: '$0',
    beginPaymentsAge: this.getValueForQuestion(this.beginPaymentsAgeNS) || null
  }

  initCalc = false

  debounceCalculations = _.debounce(() => {
    const person = this.state.headOfHousehold
    const profile = this.state.profile
    const vals = this.state.profile.pension.ncpers
    const when = utils.todaysDate()

    Accountant.getNcpersMonthlyPensionAmount({ person, vals, when }).then((mpa) => {
      const monthlyPayout = utils.toCurrency(mpa.amount)

      if (this.state.monthlyPayout !== monthlyPayout) {
        this.setState({ monthlyPayout, profile })
      }
    })
  }, 300)

  componentWillMount() {
    this.onAppStateUpdate((appState) => {
      const profile = appState.Profile.getNative('info')
      const headOfHousehold = profile.getHeadOfHousehold()
      if (!profile.pension.ncpers) {
        profile.pension.ncpers = {
          salaries: [0, 0, 0],
          yearsOfService: 0,
          formOfPayment: SimplePensionFormOfPayment.singleLifeOnly,
          beginPaymentsAge: moment.duration(65, 'years')
        }
      }

      if (profile.pension.ncpers) {
        this.debounceCalculations();
      }

      this.setState({ headOfHousehold, profile })
    })
  }

  componentWillUpdate(nextProps: IQuestionsGroupContainerProps, nextState: ICurrentPensionContainerState) {
    this.updateCalculations(nextState)
  }

  updateCalculations(nextState: ICurrentPensionContainerState) {
    const { profile, formOfPayment, headOfHousehold, yearsOfService } = nextState

    if (!profile) { return }

    profile.pension = profile.pension || {}
    nextState.beginPaymentsAge = nextState.beginPaymentsAge || moment.duration(headOfHousehold.selectedRetirementAge, 'years')

    const newNcpers = {
      salaries: [nextState.currentSalary, nextState.oneYearsAgoSalary, nextState.twoYearsAgoSalary],
      yearsOfService,
      formOfPayment,
      beginPaymentsAge: nextState.beginPaymentsAge
    }

    if (!this.initCalc || !_.isEqual(profile.pension.ncpers, newNcpers)) {
      this.initCalc = true
      profile.pension.ncpers = newNcpers
      this.debounceCalculations()
    }
  }

  updatePaymentsAge(beginPaymentsAge: moment.Duration) {
    this.state.profile.pension.ncpers.beginPaymentsAge = beginPaymentsAge
    this.setState({ beginPaymentsAge })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  render() {
    if (!this.state.headOfHousehold) { return null }
    if (!this.state.profile.pension || !this.state.profile.pension.ncpers || !this.state.beginPaymentsAge) { return null }

    const currentFormOfPayment = FormOfPaymentOptions
      .find((fopo) => fopo.value === this.state.formOfPayment)
      .label

    const beginPaymentsAgeText = utils.toNiceAge(this.state.beginPaymentsAge)
    const showSummary = this.state.monthlyPayout !== '$0'
    const lastYear = utils.todaysDate().year() - 1

    return (
      <section className="animated fadeIn">
        <h1>Current Pension</h1>
        <h5 className="lead">Please select pension amounts and types for your current pension below. </h5>
        <Row>
          <Col md={6}>
            <FormGroup>
              <h4>Salary History</h4>
              <MaterialNumber placeholder="Current Salary"
                formattedNumberMode
                name={this.currentSalaryNS}
                onChange={(e) => this.setStateAndSave({ currentSalary: this.toNumber(e.currentTarget.value) })}
                value={this.state.currentSalary} />

              <MaterialNumber placeholder={lastYear + ' Salary'}
                formattedNumberMode
                name={this.oneYearsAgoSalaryNS}
                onChange={(e) => this.setStateAndSave({ oneYearsAgoSalary: this.toNumber(e.currentTarget.value) })}
                value={this.state.oneYearsAgoSalary} />

              <MaterialNumber placeholder={(lastYear - 1) + ' Salary'}
                formattedNumberMode
                name={this.twoYearsAgoSalaryNS}
                onChange={(e) => this.setStateAndSave({ twoYearsAgoSalary: this.toNumber(e.currentTarget.value) })}
                value={this.state.twoYearsAgoSalary} />

              <MaterialNumber placeholder="Years of Service"
                formattedNumberMode
                name={this.yearsOfServiceNS}
                onChange={(e) => this.setStateAndSave({ yearsOfService: this.toNumber(e.currentTarget.value) })}
                value={this.state.yearsOfService} />
            </FormGroup>

            <FormGroup>
              <h5 >What age will you begin payments?<br /></h5>
              <RetirementAgeSelector min={55} max={70}
                width={'430px'} fontSize={'1.3em'}
                selectedValue={this.state.beginPaymentsAge} onChange={(a) => this.updatePaymentsAge(a)} />
            </FormGroup>

            <FormGroup>
              <h4>Form of Payment</h4>

              <FormOfPayment name={this.formOfPaymentNS}
                defaultValue={this.state.formOfPayment}
                options={getBrandHelpers().memberPensionFormsOfPayment}
                onChange={(formOfPayment) => this.setStateAndSave({ formOfPayment })} />
            </FormGroup>
          </Col>
          <Col md={6}>
            {showSummary && <h2 className="animated fadeIn">
              If {this.state.headOfHousehold.firstName} begins to take payments at age&nbsp;
              <a>{beginPaymentsAgeText}</a>&nbsp;
              in the form of&nbsp;
              <a>{currentFormOfPayment}</a>&nbsp;
              the monthly amount will be <a>{this.state.monthlyPayout}</a>.&nbsp;
            </h2>}

          </Col>
        </Row>
      </section >
    )
  }
}

const CurrentPensionQuestionsGroup = () => (
  <CurrentPensionContainer
    header="Current Pension" icon="" />
)

export {
  CurrentPensionQuestionsGroup,
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/Pension/NCPERS/CurrentPension/index.tsx